import React, { useState } from 'react';
import Collapse from '@mui/material/Collapse';
import {
  ListGroupNavItem,
  ListGroupButtonItem,
  SidebarNavBack,
  SidebarNavSection,
} from './ListGroupNavComponents';
import {
  useAppSelector,
  selectExerciseWorkspaceType,
} from 'slices/commonSelectors';
import { useSelectedExerciseWorkspace } from '../headers/useSelectedExerciseWorkspace';
import { useExerciseBasePath } from 'utils/useExerciseBasePath';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';

type ExerciseDropdown = 'design' | 'conduct' | 'assess' | 'settings' | 'msel';

const ExerciseNavPanel: React.FC<{ navBack: () => void }> = ({ navBack }) => {
  const [openDropdown, setOpenDropdown] = useState<ExerciseDropdown | null>(
    null
  );
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState(false);
  const workspaceType = useAppSelector(selectExerciseWorkspaceType);
  const {
    exercises,
    selectedExercise: currentExercise,
    handleSelectExerciseId: exerciseChangeHandler,
  } = useSelectedExerciseWorkspace();
  const exerciseBasePath = useExerciseBasePath();
  const toggleDropdown = (dropdown: ExerciseDropdown) => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdown);
    }
  };

  const zeroStateWorkspaceItems = (
    <>
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="grid_view"
        label="Dashboard"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="stylus_note"
        label="Design"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="event_note"
        label="MSEL"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="directions_run"
        label="Conduct"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="checklist_rtl"
        label="Assess"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="bar_chart"
        label="Analytics"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={'/none'}
        icon="settings"
        label="Settings"
      />
    </>
  );
  const teWorkspaceItems = (
    <>
      <ListGroupNavItem
        to={`${exerciseBasePath}/`}
        icon="grid_view"
        label="Dashboard"
        end
      />
      <ListGroupButtonItem
        onClick={() => toggleDropdown('design')}
        open={openDropdown === 'design'}
        icon="stylus_note"
        label="Design"
      />
      <Collapse in={openDropdown === 'design'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/experimental-event-details/details`}
          label="Experiment Details"
          secondary
          end
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/experimental-event-details/cois`}
          label="COIs"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/experimental-event-details/event_cells`}
          label="Event Cells"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/experimental-event-details/workgroups`}
          label="Workgroups"
          secondary
          isDisabled
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/experimental-event-details/data_collection`}
          label="Data Collection Methods"
          secondary
        />
      </Collapse>
      <ListGroupNavItem
        to={`${exerciseBasePath}/experimental-event-details/jelc_calendar`}
        icon="calendar_month"
        label="JELC Calendar"
      />
      <ListGroupNavItem
        to={`${exerciseBasePath}/experimental-event-details/poam`}
        icon="flag"
        label="POA&M"
      />
      <ListGroupNavItem
        to={`${exerciseBasePath}/trr`}
        icon="rate_review"
        label="TRR"
      />
      <ListGroupNavItem
        to={`${exerciseBasePath}/systems`}
        icon="settings_system_daydream"
        label="Systems"
      />
      <ListGroupButtonItem
        onClick={() => toggleDropdown('conduct')}
        open={openDropdown === 'conduct'}
        icon="directions_run"
        label="Missions"
      />
      <Collapse in={openDropdown === 'conduct'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/missions`}
          label="Missions"
          secondary
          end
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/missions/timeline`}
          label="Sorties"
          secondary
          end
        />
      </Collapse>
      <ListGroupNavItem
        to={`${exerciseBasePath}/missions/mission-timeline`}
        icon="view_timeline"
        label="Sortie Timeline"
      />
      <ListGroupNavItem
        to={`${exerciseBasePath}/missions/analytics`}
        icon="bar_chart"
        label="Analytics"
        end
      />
    </>
  );
  const dodWorkspaceItems = (
    <>
      <ListGroupNavItem
        to={`${exerciseBasePath}/`}
        icon="dashboard"
        label="Dashboard"
        end
      />
      <ListGroupButtonItem
        onClick={() => toggleDropdown('design')}
        open={openDropdown === 'design'}
        icon="stylus_note"
        label="Design"
      />
      <Collapse in={openDropdown === 'design'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/dashboard`}
          label="Overview (Dashboard)"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/exercise-details`}
          label="Exercise Details"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/participants`}
          label="Participants"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/training-objective`}
          label="Objectives"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/events`}
          label="Scenarios"
          secondary
        />
      </Collapse>
      <ListGroupButtonItem
        onClick={() => toggleDropdown('msel')}
        open={openDropdown === 'msel'}
        icon="event_note"
        label="MSEL"
      />
      <Collapse in={openDropdown === 'msel'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/msel-list`}
          label="List"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/msel-board`}
          label="Board"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/msel-calendar`}
          label="Calendar"
          secondary
        />
      </Collapse>
      <ListGroupButtonItem
        onClick={() => toggleDropdown('conduct')}
        open={openDropdown === 'conduct'}
        icon="directions_run"
        label="Conduct"
      />
      <Collapse in={openDropdown === 'conduct'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/train/dashboard`}
          label="Dashboard"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/train/msel-timeline`}
          label="MSEL Timeline"
          secondary
        />
      </Collapse>
      <ListGroupButtonItem
        onClick={() => toggleDropdown('assess')}
        open={openDropdown === 'assess'}
        icon="checklist_rtl"
        label="Assess"
      />
      <Collapse in={openDropdown === 'assess'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/assessment/overview`}
          label="Overview"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/assessment/assess-list`}
          label="Assessment List"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/feedback`}
          label="Feedback"
          secondary
        />
      </Collapse>
      <ListGroupNavItem
        to={`${exerciseBasePath}/reports/list`}
        icon="bar_chart"
        label="Analysis"
      />
    </>
  );
  const settingsItems = (
    <>
      <ListGroupButtonItem
        onClick={() => toggleDropdown('settings')}
        open={openDropdown === 'settings'}
        icon="settings"
        label="Settings"
      />
      <Collapse in={openDropdown === 'settings'}>
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/members`}
          label="Members"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/time-zone`}
          label="Time Zones"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/jmets`}
          label="JMETs"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/organizations`}
          label="Organizations"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/platform`}
          label="Platform"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/organization-type`}
          label="ESF/Organization Type"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/service`}
          label="City/Town/Service/Department"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/department-type`}
          label="Jurisdiction/Service/Department Types"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/command-agency`}
          label="State/County/Chapter/Command/Agency"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/strategic-priority`}
          label="Strategic Priorities"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/inject-method`}
          label="Inject Methods"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/inject-owner`}
          label="Inject Owners"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/status-label`}
          label="Status Labels"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/assessment-reasons`}
          label="Assessment Reasons"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/training-priority`}
          label="Objective Priority"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/exercise-type`}
          label="Exercise Type"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/fromto`}
          label="From To"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/position`}
          label="Position"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/metric`}
          label="Metric"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/primary-source`}
          label="Primary Source"
          secondary
        />
        <ListGroupNavItem
          to={`${exerciseBasePath}/settings/sub-source`}
          label="Sub Source"
          secondary
        />
        {workspaceType === 'T&E' ? (
          <>
            <ListGroupNavItem
              to={`${exerciseBasePath}/settings/support`}
              label="Support"
              secondary
            />
            <ListGroupNavItem
              to={`${exerciseBasePath}/settings/technical-discipline`}
              label="Technical Discipline"
              secondary
            />
            <ListGroupNavItem
              to={`${exerciseBasePath}/settings/mission-type`}
              label="Mission Type"
              secondary
            />
            <ListGroupNavItem
              to={`${exerciseBasePath}/settings/mission-profile`}
              label="Mission Profile"
              secondary
            />
            <ListGroupNavItem
              to={`${exerciseBasePath}/settings/mission-location`}
              label="Mission Location"
              secondary
            />
          </>
        ) : null}
      </Collapse>
    </>
  );

  const exerciseOptions =
    exercises?.map((exercise) => ({
      label: exercise.displayName,
      value: String(exercise.id),
    })) ?? [];

  return (
    <nav
      aria-label="secondary sidebar"
      className="second-panel tw:flex tw:flex-col tw:gap-1"
    >
      <SidebarNavBack
        title="Exercise Management"
        onBack={navBack}
        icon="military_tech"
      />
      <div className="tw:px-4 tw:text-base tw:font-medium">
        {showWorkspaceDropdown || !currentExercise ? (
          <SearchableSelect
            aria-label={
              currentExercise
                ? 'Select an Exercise Workspace'
                : 'Exercise Workspace'
            }
            placeholder={
              currentExercise
                ? 'Select an Exercise Workspace'
                : 'Exercise Workspace'
            }
            options={exerciseOptions}
            value={
              exerciseOptions.find(
                (option) => option.value === String(currentExercise?.id)
              ) ?? null
            }
            onChange={(next) => {
              if (!next) return;
              exerciseChangeHandler(next.value);
              setShowWorkspaceDropdown(false);
            }}
            positionerClassName="tw:z-[1100]"
            itemClassName="tw:h-auto tw:min-h-8 tw:items-start tw:whitespace-normal tw:rounded-none tw:border-b tw:border-border tw:py-1.5 tw:last:border-b-0"
          />
        ) : (
          currentExercise?.displayName || 'No Exercise Selected'
        )}
      </div>
      {currentExercise && (
        <div className="tw:px-4">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="tw:h-auto tw:px-0"
            onClick={() => setShowWorkspaceDropdown(!showWorkspaceDropdown)}
          >
            Switch Workspace
          </Button>
        </div>
      )}
      <SidebarNavSection title="Plan Exercise">
        {!currentExercise ? (
          zeroStateWorkspaceItems
        ) : (
          <>
            {workspaceType === 'T&E' ? teWorkspaceItems : dodWorkspaceItems}
            {settingsItems}
          </>
        )}
      </SidebarNavSection>
    </nav>
  );
};

export default ExerciseNavPanel;
